import { Badge, Group, Modal, Paper, Stack, Text } from '@mantine/core'
import { IconQuestionMark, IconThumbDown, IconThumbUp } from '@tabler/icons-react'
import dayjs from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'

import type { FeedbackAnnotation } from '@/services/api/api.types'

dayjs.extend(utcPlugin)

interface FeedbackDetailModalProps {
  opened: boolean
  onClose: () => void
  feedback: FeedbackAnnotation | null
  topicsData?: Array<{ id: string; displayName: string }>
}

export function FeedbackDetailModal({ opened, onClose, feedback, topicsData = [] }: FeedbackDetailModalProps) {
  if (!feedback) return null

  return (
    <Modal opened={opened} onClose={onClose} title="Feedback Details" size="lg" centered>
      <Stack gap="md">
        {/* Basic Info */}
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Date:
          </Text>
          <Text size="sm">{dayjs.utc(feedback.createdAt).local().format('MMM D, YYYY h:mm A')}</Text>
        </Group>

        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Rating:
          </Text>
          <Group gap="xs">
            {feedback.annotationKind === 'thumbs-up' ? (
              <>
                <IconThumbUp size={16} color="green" />
                <Badge color="green" variant="light" size="sm">
                  Positive
                </Badge>
              </>
            ) : feedback.annotationKind === 'thumbs-down' ? (
              <>
                <IconThumbDown size={16} color="red" />
                <Badge color="red" variant="light" size="sm">
                  Negative
                </Badge>
              </>
            ) : (
              <>
                <IconQuestionMark size={16} color="gray" />
                <Badge color="gray" variant="light" size="sm">
                  Unknown
                </Badge>
              </>
            )}
          </Group>
        </Group>

        {feedback.contentModuleTitle && (
          <Group justify="space-between" align="flex-start">
            <Text size="sm" c="dimmed">
              Content Module:
            </Text>
            <Text size="sm" style={{ maxWidth: '60%', textAlign: 'right' }}>
              {feedback.contentModuleTitle}
            </Text>
          </Group>
        )}

        {feedback.tagIds && feedback.tagIds.length > 0 && (
          <Group justify="space-between" align="flex-start">
            <Text size="sm" c="dimmed">
              Tags:
            </Text>
            <Group gap="xs" style={{ maxWidth: '60%', justifyContent: 'flex-end' }}>
              {feedback.tagIds.map((tag, index) => (
                <Badge key={tag.tagId || index} variant="light" size="sm">
                  {tag.tagName}
                </Badge>
              ))}
            </Group>
          </Group>
        )}

        {((feedback.cmTopics && feedback.cmTopics.length > 0) || feedback.topicName) && (
          <Group justify="space-between" align="flex-start">
            <Text size="sm" c="dimmed">
              {feedback.cmTopics && feedback.cmTopics.length > 1 ? 'Topics:' : 'Topic:'}
            </Text>
            <Group gap="xs" style={{ maxWidth: '60%', justifyContent: 'flex-end' }}>
              {feedback.cmTopics && feedback.cmTopics.length > 0 ? (
                feedback.cmTopics.map((topicId, index) => {
                  const topic = topicsData.find(t => t.id === topicId)
                  const topicName = topic ? topic.displayName : topicId
                  return (
                    <Badge key={index} variant="light" size="sm">
                      {topicName}
                    </Badge>
                  )
                })
              ) : feedback.topicName ? (
                <Badge variant="light" size="sm">
                  {feedback.topicName}
                </Badge>
              ) : null}
            </Group>
          </Group>
        )}

        {feedback.userFeedback && (
          <Group justify="space-between" align="flex-start">
            <Text size="sm" c="dimmed">
              User Comment:
            </Text>
            <Text size="sm" style={{ maxWidth: '60%', textAlign: 'right' }}>
              {feedback.userFeedback}
            </Text>
          </Group>
        )}

        {/* Message Details */}
        {feedback.messageText && (
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              Message:
            </Text>
            <Paper p="sm" bg="gray.0" radius="sm">
              <Text size="sm">{feedback.messageText}</Text>
            </Paper>
          </Stack>
        )}

        {/* Conversation Messages (if available) */}
        {feedback.conversationMessages && feedback.conversationMessages.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              Conversation History:
            </Text>
            <Paper p="sm" bg="gray.0" radius="sm" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <Stack gap="sm">
                {feedback.conversationMessages.map((message, index) => (
                  <Group key={index} gap="xs" align="flex-start">
                    <Badge size="xs" color={message.role === 'member' ? 'blue' : 'green'} variant="light">
                      {message.role === 'member' ? 'User' : 'Assistant'}
                    </Badge>
                    <Text size="xs" style={{ flex: 1 }}>
                      {message.text}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {dayjs.utc(message.createdAt).local().format('MMM D, h:mm A')}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Paper>
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
